import { getTrfPdfConfigFields } from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { useLaunchMode } from '@/features/auth/launchContext'
import { logger } from '@/sentry.config'
import { useAppSelector } from '@/store/hooks'
import { warnClientSideTrfRender } from '@/utils/clientTrfRenderWarning'
import { extractFormOrderData } from '@/utils/FormDataExtraction'
import { openBlankPdfWindow, renderPdfBytesInWindow } from '@/utils/pdfBytesWindow'
import { renderTestRequisitionPDFFromCanonical } from '@/utils/PDFRenderer'
import { clientTrfPdfFilename } from '@/utils/trfPdfFilename'
import { Button } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import { Check, CloseCircle, Download } from 'flowbite-react-icons/outline'
import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'

const CompletedRequestForm: React.FC = () => {
  const location = useLocation()
  // `location.state` may be the raw Epic OAuth state string, or a navigation payload
  // object that carries it as `epicState`. Extract the string form so a non-Epic router
  // payload (or a stale object) cannot be mistaken for an EHR submit.
  const rawState = location.state as string | { epicState?: string } | null | undefined
  const epicState =
    typeof rawState === 'string' ? rawState : typeof rawState?.epicState === 'string' ? rawState.epicState : ''
  const state = epicState
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const { config } = useUiConfig()
  const { mode: launchMode } = useLaunchMode()
  // Treat a present Epic OAuth state as authoritative for the EHR flow, falling back to
  // the current browser launch mode. Only the extracted `epicState` string counts here —
  // an arbitrary truthy router payload must not force EHR Section C/D logic.
  const isEhrOrder = Boolean(epicState) || launchMode === 'epic_smart'
  const { canonicalOrder: storedCanonical } = useAppSelector((s) => s.canonicalOrder)

  const handleGeneratePDF = async () => {
    return Sentry.startSpan(
      {
        op: 'ui.click',
        name: 'Generate PDF from Completed Form',
      },
      async (span) => {
        span.setAttribute('form.testType', storedCanonical?.order?.selectedOption ?? '')
        setIsGeneratingPDF(true)
        setPdfError(null)

        // The tab is claimed in the click's own tick: opening it after the PDF
        // has been generated would make it an unsolicited popup.
        const pdfWindow = openBlankPdfWindow()
        if (!pdfWindow) {
          span.setAttribute('error', true)
          span.setAttribute('error.message', 'Popup blocked')
          Sentry.captureException(new Error('Popup blocked. Please allow popups for this site.'), {
            tags: { operation: 'ui.action.pdf_open' },
          })
          setPdfError('Popups are blocked for this site. Allow them to open the requisition PDF.')
          setIsGeneratingPDF(false)
          return
        }

        try {
          const orderFormData = await extractFormOrderData(state)
          if (!storedCanonical) throw new Error('No canonical order data available for PDF generation.')
          // This route has no server-rendered equivalent: it holds no order or session id.
          warnClientSideTrfRender({ reason: 'completed_form_route', renderer: 'canonical' })
          const pdfBytes = await renderTestRequisitionPDFFromCanonical(
            storedCanonical,
            orderFormData,
            getTrfPdfConfigFields(config),
            isEhrOrder
          )
          span.setAttribute('pdf.size', pdfBytes.length)

          // Paint the PDF into the spawned tab as canvases with our own
          // Download/Print controls. Epic's embedded browser has no PDF plugin,
          // so a native viewer (blob/data URI/served .pdf) shows a blank tab —
          // painting the pixels ourselves is the only thing that displays.
          await renderPdfBytesInWindow(pdfWindow, pdfBytes, { filename: clientTrfPdfFilename() })
        } catch (error) {
          pdfWindow.close()
          span.setAttribute('error', true)
          if (error instanceof Error) {
            span.setAttribute('error.message', error.message)
          }
          logger.error('Completed form PDF generation failed', {
            error: error instanceof Error ? error.message : String(error),
          })
          Sentry.captureException(error, { tags: { operation: 'ui.action.pdf_generation' } })
          setPdfError(error instanceof Error ? error.message : 'Failed to generate PDF')
        } finally {
          setIsGeneratingPDF(false)
        }
      }
    )
  }

  return (
    <div className="min-h-screen bg-background-secondary-medium py-8 dark:bg-gray-800">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-100 dark:bg-gray-900">
            <Check className="h-6 w-6 text-text-fg-success dark:text-success-100" />
          </div>
          <h1 className="mt-4 text-3xl font-bold text-text-heading dark:text-white">Form Complete!</h1>
          <p className="mt-2 text-lg text-text-body dark:text-gray-400">Your requisition form has been successfully completed.</p>
        </div>

        {/* Actions */}
        <div className="rounded-base bg-white shadow dark:bg-gray-900">
          <div className="px-6 py-8">
            {pdfError && (
              <div className="mb-6 rounded-base bg-error-50 p-4 dark:bg-gray-800">
                <div className="flex">
                  <CloseCircle className="h-5 w-5 text-text-fg-error dark:text-error-100" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-text-fg-error-strong dark:text-error-100">Error</h3>
                    <p className="mt-1 text-sm text-text-fg-error dark:text-error-100">{pdfError}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleGeneratePDF}
                disabled={isGeneratingPDF}
                className="focus-visible:ring-brand-600"
              >
                {isGeneratingPDF ? (
                  <>
                    {/* helix-exception: Helix exports no spinner without role/label changes (F-13) */}
                    <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="h-5 w-5" />
                    Open PDF
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Simple next steps */}
        <div className="mt-6 rounded-base bg-info-50 p-4 dark:bg-gray-900">
          <p className="text-sm text-info-600 dark:text-info-100">
            <strong>Next:</strong> Download your PDF, print it, have it signed, and submit according to your
            institution's procedures.
          </p>
        </div>
      </div>
    </div>
  )
}

export default CompletedRequestForm
